import { type ReactNode } from 'react'

import { clsx } from 'clsx'

import { OptionsList } from './OptionsList'

import type { SelectOption } from './types'

interface DesktopModalProps<T> {
  dropdownRef: React.RefObject<HTMLDivElement>
  placeholder: string
  searchPlaceholder: string
  searchQuery: string
  filteredOptions: SelectOption<T>[]
  value: T | null | undefined
  highlightedIndex: number
  isLoading: boolean
  noOptionsText: string
  virtualized: boolean
  itemHeight: number
  maxVisibleItems: number
  listHeight: number
  totalHeight: number
  startIndex: number
  endIndex: number
  offsetY: number
  resolvedFooterAction?: ReactNode
  searchInputRef: React.Ref<HTMLInputElement>
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  onSearchChange: (value: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
  onSelect: (option: SelectOption<T>) => void
  onSetHighlightedIndex: (index: number) => void
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void
  onClose: () => void
}

function ModalSearchBar({ searchInputRef, searchPlaceholder, searchQuery, onSearchChange, onKeyDown }: {
  searchInputRef: React.Ref<HTMLInputElement>
  searchPlaceholder: string
  searchQuery: string
  onSearchChange: (v: string) => void
  onKeyDown: (e: React.KeyboardEvent) => void
}): JSX.Element {
  return (
    <div className="p-3 border-b border-border flex-shrink-0">
      <input
        ref={searchInputRef}
        className="el-input el-input-sm bg-secondary"
        placeholder={searchPlaceholder}
        type="text"
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        onKeyDown={onKeyDown}
      />
    </div>
  )
}

function ModalOptionsListBlock<T>(p: DesktopModalProps<T>): JSX.Element {
  return (
    <div className="flex-1 overflow-hidden">
      <OptionsList
        endIndex={p.endIndex}
        filteredOptions={p.filteredOptions}
        highlightedIndex={p.highlightedIndex}
        isLoading={p.isLoading}
        isMobile={false}
        itemHeight={p.itemHeight}
        listHeight={p.listHeight}
        maxVisibleItems={p.maxVisibleItems}
        noOptionsText={p.noOptionsText}
        offsetY={p.offsetY}
        renderOption={p.renderOption}
        startIndex={p.startIndex}
        totalHeight={p.totalHeight}
        value={p.value}
        virtualized={p.virtualized}
        onScroll={p.onScroll}
        onSelect={p.onSelect}
        onSetHighlightedIndex={p.onSetHighlightedIndex}
      />
    </div>
  )
}

const MODAL_CLASS = clsx(
  'relative z-10',
  'bg-popover text-popover-foreground',
  'border border-border',
  'rounded-lg shadow-2xl',
  'animate-scale-in',
  'overflow-hidden',
  'w-full max-w-md',
  'flex flex-col',
  'max-h-[80vh]',
)

export function DesktopModal<T>(p: DesktopModalProps<T>): JSX.Element {
  const handleBackdropKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Escape') { p.onClose() }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/50 animate-fade-in"
        role="presentation"
        onClick={p.onClose}
        onKeyDown={handleBackdropKeyDown}
      />
      <div ref={p.dropdownRef} aria-modal="true" className={MODAL_CLASS} role="dialog">
        <ModalHeader placeholder={p.placeholder} onClose={p.onClose} />
        <ModalSearchBar
          searchInputRef={p.searchInputRef}
          searchPlaceholder={p.searchPlaceholder}
          searchQuery={p.searchQuery}
          onKeyDown={p.onKeyDown}
          onSearchChange={p.onSearchChange}
        />
        <ModalOptionsListBlock {...p} />
        {p.resolvedFooterAction && (
          <div className="border-t border-border p-3 flex-shrink-0">{p.resolvedFooterAction}</div>
        )}
      </div>
    </div>
  )
}

interface ModalHeaderProps {
  placeholder: string
  onClose: () => void
}

function ModalHeader({ placeholder, onClose }: ModalHeaderProps): JSX.Element {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-border flex-shrink-0">
      <h2 className="text-lg font-semibold text-foreground">{placeholder}</h2>
      <button
        className="p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
        type="button"
        onClick={onClose}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      </button>
    </div>
  )
}
